<template>
    <section class="py-8 bg-white lg:py-24">
      <div class="max-w-5xl px-4 mx-auto">
        <!-- Title -->
        <div class="max-w-3xl mx-auto text-xl text-center text-black lg:px-4 lg:text-3xl sm:text-2xl xl:text-4xl font-ww-medium" v-html="title"></div>
  
        <!-- Description -->
        <div class="max-w-3xl mx-auto mt-6 text-center text-black sm:text-lg xl:text-xl font-ww-light" v-html="description"></div>
  
        <!-- Main Image -->
        <div class="mt-10 lg:mt-16 lg:px-4 xl:px-0">
          <nuxt-img
            :src="mainImageSrc"
            :alt="mainImageAlt"
            sizes="xs:330px sm:440px md:760px lg:1024px xl:990px 2xl:990px"
            loading="lazy"
            format="webp"
            width="990"
            height="519"
            class=""
          />
        </div>
  
        <!-- Features Section -->
        <div class="px-4 mt-8 lg:mt-16 xl:px-0">
          <div class="text-xl font-bold text-center text-black sm:text-xl md:text-2xl xl:text-3xl" v-html="featuresTitle"></div>
  
          <div class="grid grid-cols-12 mt-12 gap-x-6 lg:gap-x-12 gap-y-8">
            <div
              v-for="(feature, index) in features"
              :key="index"
              class="flex flex-col col-span-12 sm:col-span-6 lg:col-span-4 gap-y-4"
            >
              <nuxt-img
                :src="feature.imgSrc"
                :alt="feature.alt"
                sizes="xs:330px sm:440px md:760px lg:1024px xl:1200px 2xl:1400px"
                loading="lazy"
                format="webp"
                width="44"
                height="49"
                class="mx-auto lg:mx-0"
              />
              <div class="text-lg text-center text-black font-ww-medium sm:text-21 lg:text-left">{{ feature.title }}</div>
              <ul class="pl-6 list-disc sm:mx-auto xl:text-xl sm:text-lg font-ww-light lg:mx-0 md-max:max-w-60">
                <li v-for="(item, itemIndex) in featureItems(feature)" :key="`feature-${index}-item-${itemIndex}`" class="text-black">{{ item }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  // Nested lists inside a Builder list render as ObservableMap and cannot be edited.
  // Items are a textarea (one bullet per line). Older entries stored an items array.
  const featureItems = (feature) => {
    if (typeof feature?.itemsText === 'string' && feature.itemsText.trim()) {
      return feature.itemsText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    }

    const raw = feature?.items;
    const list = Array.isArray(raw) ? raw : [];

    return list
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item.get === 'function') return item.get('text') || '';
        return item?.text || '';
      })
      .map((line) => String(line).trim())
      .filter(Boolean);
  };

  const props = defineProps({
    title: {
      type: String,
      default: 'Introducing the Lifeboost <br> Anti-Inflation Shield Subscription',
    },
    description: {
      type: String,
      default: `Now, for the first time ever, you can lock in today's prices on our premium coffee AND earn rewards with every order.`,
    },
    mainImageSrc: {
      type: String,
      default: '/assets/images/8th-free.png',
    },
    mainImageAlt: {
      type: String,
      default: 'Anti-Inflation Shield Image',
    },
    featuresTitle: {
      type: String,
      default: "Here's Everything You Get:",
    },
    features: {
      type: Array,
      default: () => [
        {
          imgSrc: '/assets/images/black_icon.png',
          alt: 'Price Protection',
          title: '1. Price Protection',
          itemsText: "Lock in current prices for 12 months\nProtection against industry price increases\nPremium coffee at today's rates",
        },
        {
          imgSrc: '/assets/images/black_icon_2.png',
          alt: 'Digital Rewards',
          title: '2. Digital Rewards',
          itemsText: 'Automatic punch card tracking\nEvery 8th bag FREE\n$0 shipping on all orders\nEarn rewards on any roast',
        },
        {
          imgSrc: '/assets/images/black_icon_3.png',
          alt: 'Premium Service',
          title: '3. Premium Service',
          itemsText: 'Small Batch Roasted\nDelivered to your door\nFlexible delivery schedule\nEasy to modify or cancel',
        },
      ],
    },
  });
  
  // Builder.io configuration
  defineBuilderComponent({
    friendlyName: 'FeatureWithImageAndBenefits',
    inputs: [
      {
        name: 'title',
        type: 'richText',
        friendlyName: 'Section Title',
        defaultValue: 'Introducing the Lifeboost <br> Anti-Inflation Shield Subscription',
      },
      {
        name: 'description',
        type: 'richText',
        friendlyName: 'Section Description',
        defaultValue: `Now, for the first time ever, you can lock in today's prices on our premium coffee AND earn rewards with every order.`,
      },
      {
        name: 'mainImageSrc',
        type: 'file',
        friendlyName: 'Main Image Source',
        allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
        defaultValue: '/assets/images/8th-free.png',
      },
      {
        name: 'mainImageAlt',
        type: 'text',
        friendlyName: 'Main Image Alt Text',
        defaultValue: 'Anti-Inflation Shield Image',
      },
      {
        name: 'featuresTitle',
        type: 'richText',
        friendlyName: 'Features Title',
        defaultValue: "Here's Everything You Get:",
      },
      {
        name: 'features',
        type: 'list',
        friendlyName: 'Features List',
        subFields: [
          {
            name: 'imgSrc',
            type: 'file',
            friendlyName: 'Feature Image Source',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          },
          {
            name: 'alt',
            type: 'text',
            friendlyName: 'Feature Image Alt Text',
          },
          {
            name: 'title',
            type: 'text',
            friendlyName: 'Feature Title',
          },
          {
            name: 'itemsText',
            type: 'longText',
            friendlyName: 'Feature Items',
            helperText: 'One bullet per line',
          },
        ],
        defaultValue: [
          {
            imgSrc: '/assets/images/black_icon.png',
            alt: 'Price Protection',
            title: '1. Price Protection',
            itemsText: "Lock in current prices for 12 months\nProtection against industry price increases\nPremium coffee at today's rates",
          },
          {
            imgSrc: '/assets/images/black_icon_2.png',
            alt: 'Digital Rewards',
            title: '2. Digital Rewards',
            itemsText: 'Automatic punch card tracking\nEvery 8th bag FREE\n$0 shipping on all orders\nEarn rewards on any roast',
          },
          {
            imgSrc: '/assets/images/black_icon_3.png',
            alt: 'Premium Service',
            title: '3. Premium Service',
            itemsText: 'Small Batch Roasted\nDelivered to your door\nFlexible delivery schedule\nEasy to modify or cancel',
          },
        ],
      },
    ],
  });
  </script>
  
